import { hashKey, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { Bookmark } from 'lucide-react'
import { useState } from 'react'

import { isApiError } from '@/api/client'
import { keys } from '@/api/keys'
import { setStoryBookmark, type StoryBookmark, type StoryListResponse, type StoryRow } from '@/api/stories'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/toast'
import { WithTooltip } from '@/components/ui/tooltip'
import { cn } from '@/components/ui/utils'

export const BOOKMARK_STORY_MUTATION = ['stories', 'bookmark'] as const

/** What the button needs: the story's uid, its title (for the button's name) and whether it is bookmarked. */
export type BookmarkTarget = Pick<StoryRow, 'uid' | 'title'> & { bookmarked?: boolean }

interface BookmarkVars {
  uid: string
  title: string
  bookmarked: boolean
}

type BookmarkState = Pick<StoryBookmark, 'bookmarked' | 'bookmarked_at'>

/** Where a story sat in each cached list (by query hash), to put it back in a list it left. */
type Places = Map<string, { row: StoryRow; index: number }>

/**
 * One story's toggles still out. Only the newest may write the server's answer, so two quick clicks
 * end on the second one even when the first one's answer arrives last. `base` is the last state the
 * server is known to hold (what the page showed before the first click, then each answer, the
 * latest-sent one winning): a refused toggle goes back to it, for this story only, so another
 * story's toggle (still out, or saved) is never undone along with it.
 */
interface Run {
  newest: number
  pending: number
  base: BookmarkState
  baseFrom: number
  failed: boolean
  /** The story is gone (a 404): never put it back in a list. */
  gone: boolean
  places: Places
}
const runs = new Map<string, Run>()
let seq = 0

/** A new run: the state every cached copy shows before this click, and where the story sits. */
function startRun(qc: QueryClient, uid: string, was: boolean, id: number): Run {
  const places: Places = new Map()
  let at: string | null | undefined = qc.getQueryData<StoryRow>(keys.stories.detail(uid))?.bookmarked_at
  for (const [key, old] of qc.getQueriesData<StoryListResponse>({ queryKey: keys.stories.list() })) {
    const index = old?.items.findIndex((s) => s.uid === uid) ?? -1
    if (index < 0) continue
    const row = old!.items[index]!
    places.set(hashKey(key), { row, index })
    at ??= row.bookmarked_at
  }
  return { newest: id, pending: 0, base: { bookmarked: was, bookmarked_at: was ? (at ?? null) : null }, baseFrom: 0, failed: false, gone: false, places }
}

/**
 * Write a bookmark into every cached copy of the story: each `keys.stories.list()` page (a page
 * filtered by `bookmarked` drops a story that no longer matches) and its `keys.stories.detail()`.
 * With `places` (going back after a refusal) a filtered page the story had left gets it back, where
 * it was.
 */
function writeBookmark(qc: QueryClient, uid: string, next: BookmarkState, places?: Places): void {
  for (const [key, old] of qc.getQueriesData<StoryListResponse>({ queryKey: keys.stories.list() })) {
    if (!old) continue
    const filter = (key[2] ?? {}) as { bookmarked?: unknown }
    const belongs = typeof filter.bookmarked !== 'boolean' || filter.bookmarked === next.bookmarked
    let items: StoryRow[]
    if (old.items.some((s) => s.uid === uid)) {
      items = belongs ? old.items.map((s) => (s.uid === uid ? { ...s, ...next } : s)) : old.items.filter((s) => s.uid !== uid)
    } else {
      const was = belongs ? places?.get(hashKey(key)) : undefined
      if (!was) continue
      items = [...old.items]
      items.splice(Math.min(was.index, items.length), 0, { ...was.row, ...next })
    }
    qc.setQueryData<StoryListResponse>(key, { ...old, items, total: Math.max(0, old.total + items.length - old.items.length) })
  }
  qc.setQueryData<StoryRow>(keys.stories.detail(uid), (old) => (old ? { ...old, ...next } : old))
}

/**
 * Bookmark or un-bookmark a story. Optimistic: every cached list and the story's page change at
 * once, go back if the server refuses (with a toast saying so), and the lists are re-fetched either
 * way so a "Bookmarked" view and "bookmarked first" order settle on the server's answer.
 */
export function useBookmarkStory() {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: BOOKMARK_STORY_MUTATION,
    mutationFn: ({ uid, bookmarked }: BookmarkVars) => setStoryBookmark(uid, bookmarked),
    onMutate: async ({ uid, bookmarked }) => {
      const id = ++seq
      // Read before this click writes anything; a click while a run is out joins it.
      const run = runs.get(uid) ?? startRun(qc, uid, !bookmarked, id)
      runs.set(uid, run)
      run.newest = id
      run.pending += 1
      await Promise.all([qc.cancelQueries({ queryKey: keys.stories.list() }), qc.cancelQueries({ queryKey: keys.stories.detail(uid) })])
      writeBookmark(qc, uid, { bookmarked, bookmarked_at: bookmarked ? new Date().toISOString() : null })
      return { id }
    },
    onError: (err, { uid, title, bookmarked }, context) => {
      const run = runs.get(uid)
      if (run) {
        run.failed = true
        if (isApiError(err) && err.isNotFound) run.gone = true
        if (run.newest === context?.id) writeBookmark(qc, uid, run.base, run.gone ? undefined : run.places)
      }
      toast.error(bookmarked ? `Could not bookmark “${title}”` : `Could not remove the bookmark from “${title}”`, {
        description: err instanceof Error ? err.message : String(err),
      })
    },
    onSuccess: (saved, { uid }, context) => {
      const run = runs.get(uid)
      if (!run || !context) return
      if (context.id >= run.baseFrom) {
        run.base = { bookmarked: saved.bookmarked, bookmarked_at: saved.bookmarked_at }
        run.baseFrom = context.id
      }
      if (run.newest === context.id) writeBookmark(qc, uid, saved)
    },
    onSettled: (_saved, _err, { uid }) => {
      const run = runs.get(uid)
      if (run && --run.pending === 0) {
        runs.delete(uid)
        // A refusal in the run: end on what the server holds, whichever answer came last.
        if (run.failed) writeBookmark(qc, uid, run.base, run.gone ? undefined : run.places)
      }
      return qc.invalidateQueries({ queryKey: keys.stories.list() })
    },
  })
}

export interface BookmarkButtonProps {
  story: BookmarkTarget
  /**
   * `icon` (story cards): a square icon button; its name carries the story's title and a tooltip
   * says what it does. `label` (the story's own page): the icon and the words.
   */
  appearance?: 'icon' | 'label'
  className?: string
  /** After a click, with the state it asked for (the request may still fail and roll back). */
  onToggle?: (bookmarked: boolean) => void
}

/**
 * The bookmark toggle. Plain words: "Bookmark" when it is not bookmarked, "Remove bookmark" when it
 * is; a filled bookmark shows the state. A real `<button>`, so Enter and Space work, and a polite
 * live region says what happened ("Bookmarked" / "Bookmark removed").
 */
export function BookmarkButton({ story, appearance = 'icon', className, onToggle }: BookmarkButtonProps) {
  const bookmark = useBookmarkStory()
  const [said, setSaid] = useState('')
  const on = story.bookmarked === true
  const action = on ? 'Remove bookmark' : 'Bookmark'

  const toggle = () => {
    const next = !on
    bookmark.mutate({ uid: story.uid, title: story.title, bookmarked: next })
    setSaid(next ? 'Bookmarked' : 'Bookmark removed')
    onToggle?.(next)
  }

  const icon = <Bookmark aria-hidden className={cn(on && 'fill-current text-accent')} />
  const status = (
    <span className="sr-only" role="status" aria-live="polite">
      {said}
    </span>
  )

  if (appearance === 'label')
    return (
      <>
        <Button variant="ghost" size="sm" className={cn(on && 'text-ink', className)} onClick={toggle} data-bookmarked={on || undefined}>
          {icon}
          {action}
        </Button>
        {status}
      </>
    )

  return (
    <>
      <WithTooltip label={action}>
        <Button
          variant="ghost"
          size="sm"
          className={cn('w-9 px-0', on ? 'text-accent' : 'text-ink-2', className)}
          aria-label={on ? `Remove bookmark from “${story.title}”` : `Bookmark “${story.title}”`}
          onClick={toggle}
          data-bookmarked={on || undefined}
        >
          {icon}
        </Button>
      </WithTooltip>
      {status}
    </>
  )
}
